import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetOrderPlan } from "@/services/order/queries";
import { getTimeSinceText } from "@/utils/timestamp";
import { Chip, DataTable, Icon, useServerTableState } from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { type PlanStatus, type PlanStep, groupPlan, planStatus } from "./plan";

const statusStyle = {
	pending: { variant: "muted", icon: "time-line" },
	replaced: { variant: "muted", icon: "skip-forward-line" },
	notRun: { variant: "muted", icon: "stop-circle-line" },
	running: { variant: "info", icon: "loader-4-line" },
	success: { variant: "success", icon: "checkbox-circle-line" },
	missed: { variant: "error", icon: "close-circle-line" },
	failed: { variant: "warning", icon: "error-warning-line" },
	unsold: { variant: "warning", icon: "error-warning-line" },
} as const;

export function PlanResults({
	steps,
	latestBatchId,
}: { steps: PlanStep[]; latestBatchId?: string }) {
	const { t, lang } = useTranslation();
	const number = new Intl.NumberFormat(lang);
	const counts = new Map<PlanStatus, number>();
	for (const step of steps) {
		const status = planStatus(step, latestBatchId);
		counts.set(status, (counts.get(status) ?? 0) + 1);
	}
	return (
		<div className="flex flex-wrap gap-1.5">
			{Array.from(counts, ([status, count]) => (
				<Chip
					key={status}
					variant={statusStyle[status].variant}
					className="gap-1 whitespace-nowrap"
				>
					<Icon name={statusStyle[status].icon} size="sm" aria-hidden="true" />
					{steps.length > 1 ? `${number.format(count)} · ` : ""}
					{t(`orderPlan.status.${status}`)}
				</Chip>
			))}
		</div>
	);
}

type PlanRow = {
	steps: PlanStep[];
	start: number;
	end: number;
	planned: number;
	delivered: number;
};

export default function OrderDetailsPlan({
	orderId,
	live,
}: { orderId: string; live: boolean }) {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const { data, isLoading, isFetching, error, refetch } = useGetOrderPlan(
		orderId,
		live,
	);
	const server = useServerTableState();
	const latestBatchId = data?.steps.at(-1)?.batch_id;
	const rows = useMemo(() => {
		let position = 0;
		let batchId: string | undefined;
		// replaced steps never ran and a newer batch took over: not worth showing
		const steps = (data?.steps ?? []).filter(
			(step) => planStatus(step, latestBatchId) !== "replaced",
		);
		const rows = groupPlan(steps).map((steps) => {
			// numbering restarts with each batch
			if (steps[0].batch_id !== batchId) {
				batchId = steps[0].batch_id;
				position = 0;
			}
			const start = position + 1;
			position += steps.length;
			return {
				steps,
				start,
				end: position,
				planned: steps.reduce((sum, step) => sum + step.to_transfer, 0),
				delivered: steps.reduce((sum, step) => sum + step.transferred, 0),
			};
		});
		return rows.reverse(); // newest first
	}, [data, latestBatchId]);
	const columns = useMemo<ColumnDef<PlanRow>[]>(() => {
		const number = new Intl.NumberFormat(lang);
		const columns: ColumnDef<PlanRow>[] = [
			{
				id: "step",
				header: t("orderPlan.columns.step"),
				cell: ({ row }) =>
					row.original.start === row.original.end
						? number.format(row.original.start)
						: `${number.format(row.original.start)}–${number.format(
								row.original.end,
							)}`,
			},
			{
				id: "method",
				header: t("orderPlan.columns.method"),
				cell: ({ row }) => {
					const { steps } = row.original;
					return (
						<span className="font-medium">
							{steps[0].method === "QUICK_SELL"
								? t("orderPlan.quickSells", { count: steps.length })
								: t("orderPlan.snipe")}
						</span>
					);
				},
			},
			{
				id: "player",
				header: t("orderPlan.columns.player"),
				cell: ({ row }) => {
					const first = row.original.steps[0];
					return first.method === "QUICK_SELL" ? (
						"—"
					) : (
						<span>
							{first.player_name}{" "}
							<span className="text-secondary-text-100">
								· {first.player_rating}
							</span>
						</span>
					);
				},
			},
			{
				id: "time",
				header: t("orderPlan.columns.time"),
				meta: { className: "whitespace-nowrap" },
				cell: ({ row }) =>
					getTimeSinceText(row.original.steps[0].created_at, t, lang),
			},
			{
				id: "sender",
				header: t("orderPlan.columns.sender"),
				cell: ({ row }) => {
					const first = row.original.steps[0];
					return first.method === "QUICK_SELL" ? (
						"—"
					) : (
						<div>
							<div dir="ltr">{first.sender_email}</div>
							<div className="mt-1 text-xs text-secondary-text-100">
								{t("orderPlan.priority")}:{" "}
								{first.sender_priority
									? t(`orderPlan.priorities.${first.sender_priority}`)
									: "—"}
							</div>
						</div>
					);
				},
			},
			{
				id: "successRate",
				header: t("orderPlan.columns.successRate"),
				cell: ({ row }) => {
					const first = row.original.steps[0];
					return first.method === "QUICK_SELL" ? (
						"—"
					) : (
						<div>
							{number.format(first.player_success_ratio ?? 0)}%
							{first.player_has_history === false && (
								<div className="mt-1 text-xs text-secondary-text-100">
									{t("orderPlan.noHistory")}
								</div>
							)}
						</div>
					);
				},
			},
			{
				id: "planned",
				header: t("orderPlan.columns.planned"),
				meta: { className: "text-end tabular-nums" },
				cell: ({ row }) => number.format(row.original.planned),
			},
			{
				id: "delivered",
				header: t("orderPlan.columns.delivered"),
				meta: { className: "text-end tabular-nums" },
				cell: ({ row }) =>
					row.original.steps.some((step) => step.state !== null)
						? number.format(row.original.delivered)
						: "—",
			},
			{
				id: "result",
				header: t("orderPlan.columns.result"),
				cell: ({ row }) => {
					const { steps } = row.original;
					const reasons = [
						...new Set(steps.flatMap((step) => step.reason ?? [])),
					];
					return (
						<div>
							<PlanResults steps={steps} latestBatchId={latestBatchId} />
							{reasons.map((reason) => (
								<div
									key={reason}
									className="mt-2 text-xs text-secondary-text-100"
								>
									{reason}
								</div>
							))}
						</div>
					);
				},
			},
		];
		return columns.map((column) => ({ ...column, enableSorting: false }));
	}, [t, lang, latestBatchId]);
	const pageIndex = Math.min(
		server.pageIndex,
		Math.max(0, Math.ceil(rows.length / server.pageSize) - 1),
	);

	return (
		<DataTable
			columns={columns}
			data={rows.slice(
				pageIndex * server.pageSize,
				(pageIndex + 1) * server.pageSize,
			)}
			totalItems={rows.length}
			server={{ ...server, pageIndex }}
			pageSizeOptions={[10, 20, 40, 100]}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error ? getErrorInterface(error) : null}
			onRetry={refetch}
			getRowId={(row) => row.steps[0].id}
			emptyStateProps={{
				title: t("orderPlan.emptyTitle"),
				description: t("orderPlan.emptyDescription"),
				mode: "data",
			}}
		/>
	);
}
